<script setup lang="ts">
import { useBetbook } from "~/store/betbook";
import sportUrlBuilder from "~/utils/sport/sportUrlBuilder";
import t from "~/utils/translate";
import LiveIndicator from "../../LiveIndicator.vue";
import formatScores, { formatStateText } from "~/utils/sport/formatScore";
import Icon from "~/components/ui/Icon.vue";
import { mdiClockOutline } from "@mdi/js";
import DateTime from "~/components/ui/DateTime.vue";
import Market from "~/components/layout/Sport/MoreMarkets/Market.vue";
import MarketOutcomes from "../../MoreMarkets/MarketOutcomes.vue";
import { IMarket } from "~/types/sports/market";

interface Props {
	eventId: number;
}

const store = useBetbook();
const route = useRoute();
const props = defineProps<Props>();
const event = computed(() => store.events.get(props.eventId));
const score = computed(() => store.scores.get(props.eventId));
const { fixtureType } = route.params;

const markets = computed(() => {
	return [...store.markets.values()].filter((m) => m.eventId === props.eventId);
});

const findmarket = (index: number) => {
	return markets.value.find((market) => market.name === store.displayMarkets[index].name) as IMarket;
};

const market = computed(() => findmarket(0));

const outcomes = computed(() => {
	try {
		if (findmarket(0)) {
			const outcomes = [...store.outcomes.values()];
			const outcomesFiltered = outcomes.filter((o) => o.marketId === market.value?.marketId).sort((a, b) => a.index - b.index);
			return outcomesFiltered;
		} else return undefined;
	} catch (e) {
		return null;
	}
});

const isDisabled = computed(() => {
	return (
		!market.value || !outcomes || market.value.isSuspended || !event.value?.isActive || !event.value?.shouldDisplay || !market.value.shouldDisplay
	);
});
</script>

<template>
	<NuxtLink
		rel="ugc noindex nofollow"
		:href="
			sportUrlBuilder({
				fixtureType: fixtureType,
				sport: event?.sportId,
				event: {
					awayTeam: event?.awayTeam ?? '',
					homeTeam: event?.homeTeam ?? '',
					eventId: event?.eventId ?? 0,
					leagueId: event?.leagueId ?? '',
					regionId: event?.regionId ?? '',
					expectedEndEpoch: event?.expectedEndEpoch ?? 0,
					expectedStartEpoch: event?.expectedStartEpoch ?? 0,
				},
			})
		"
		class="grid grid-cols-11 px-2 py-3 text-xs leading-4 bg-light-50 dark:bg-dark-800 base-text"
	>
		<div class="flex flex-col col-span-9 gap-1">
			<div class="overflow-hidden text-sm font-bold whitespace-nowrap text-ellipsis">
				{{ event?.homeTeam }}&nbsp;&nbsp;vs&nbsp;&nbsp;{{ event?.awayTeam }}
			</div>
			<div class="flex gap-1 text-xs whitespace-nowraps whitespace-nowrap">
				<div v-if="event?.isLive" class="flex">
					<LiveIndicator />
					<span v-if="score">{{ formatScores({ sportId: event.sportId, scoreboard: score }) }}</span>
				</div>
				<div class="flex" v-else>
					<Icon :path="mdiClockOutline" class="w-4 h-4 fill-dark-300 dark:fill-light-600" />
					<DateTime dateFormat="dd MMM -" timeFormat=" HH:mm" time :date="event?.expectedStartEpoch || ''" />
				</div>
				<div class="overflow-hidden whitespace-nowrap text-ellipsis">
					<span>{{ event?.league }}, {{ event?.region }}</span>
				</div>
			</div>
		</div>
		<div class="flex items-center justify-end col-span-2">
			<div
				class="rounded-md border-[1px] border-light-500 text-[10px] font-bold text-dark-700 leading-[12px] text-center p-1 capitalize dark:text-light-50 dark:border-dark-500"
			>
				{{ t("more") }}<br />
				{{ t("bets") }}
			</div>
		</div>
		<div class="col-span-11 mt-3" v-if="!isDisabled">
			<MarketOutcomes
				v-for="(_, index) in store.displayMarkets"
				isSimpleView
				:eventId="eventId"
				:marketId="findmarket(index)?.marketId"
				:market="findmarket(index)"
				:selectedMarket="null"
				:buildABetSelections="[]"
				@handleBuildABetSelection=""
			/>
		</div>
		<div
			:class="[
				'col-span-11 mt-3 flex items-center h-8 px-[6px] gap-1 text-xs leading-3 overflow-hidden border border-light-500 dark:border-dark-500 text-center rounded-md select-none justify-center',
				market?.isSuspended && 'bg-light-500 dark:bg-dark-400',
			]"
			v-else
		>
			<span v-if="market?.isSuspended">{{ t("suspended") }}</span>
			<span v-else>{{ t("more-bets") }}</span>
		</div>
	</NuxtLink>
</template>
